import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { authFetch } from "../utils/authFetch";

const TIPI_ATTIVITA = [
  { value: "VISITA", label: "Visita" },
  { value: "TELEFONATA", label: "Telefonata" },
  { value: "EMAIL", label: "Email" },
  { value: "ALTRO", label: "Altro contatto" },
];

function formatDate(value) {
  if (!value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleString("it-IT", {
        dateStyle: "medium",
        timeStyle: "short",
      });
}

function activityLabel(tipo) {
  return TIPI_ATTIVITA.find((item) => item.value === tipo)?.label || tipo;
}

export default function AppAgenteCrmPage() {
  const { code } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [tipo, setTipo] = useState("TELEFONATA");
  const [notes, setNotes] = useState("");
  const [nextContact, setNextContact] = useState("");

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const endpoint = code
        ? `/api/agente/aziende/${encodeURIComponent(code)}/crm/`
        : "/api/agente/promemoria/";

      const response = await authFetch(endpoint);
      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          result?.detail || "Impossibile caricare i dati commerciali."
        );
      }

      setData(result);
    } catch (err) {
      setError(err?.message || "Errore durante il caricamento.");
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const registraAttivita = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");

    if (!notes.trim()) {
      setError("Inserisci le note del contatto.");
      return;
    }

    if (nextContact) {
      const appuntamento = new Date(nextContact);
      if (
        Number.isNaN(appuntamento.getTime()) ||
        appuntamento.getTime() <= Date.now()
      ) {
        setError("Il prossimo appuntamento deve essere nel futuro.");
        return;
      }
    }

    setSaving(true);

    try {
      const payload = {
        tipo,
        notes: notes.trim(),
      };

      if (nextContact) {
        payload.next_contact_at = new Date(nextContact).toISOString();
      }

      const response = await authFetch(
        `/api/agente/aziende/${encodeURIComponent(code)}/crm/attivita/`,
        {
          method: "POST",
          json: payload,
        }
      );

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(result?.detail || "Registrazione non riuscita.");
      }

      setNotes("");
      setNextContact("");
      setMessage("Attività commerciale registrata correttamente.");
      await loadData();
    } catch (err) {
      setError(err?.message || "Errore durante il salvataggio.");
    } finally {
      setSaving(false);
    }
  };

  const cardStyle = {
    border: 0,
    borderRadius: 18,
    boxShadow: "0 4px 18px rgba(0,0,0,0.07)",
  };

  const goHome = () => navigate("/app");

  return (
    <div className="container py-4" style={{ maxWidth: 760 }}>
      <div className="d-flex align-items-center justify-content-between gap-3 mb-4">
        <div>
          <div className="small text-uppercase text-muted fw-bold">
            Area Agente
          </div>
          <h2 className="fw-bold mb-1">
            {code ? "Scheda commerciale" : "Promemoria commerciali"}
          </h2>
          <div className="text-muted small">Dallo Zio Roby</div>
        </div>

        <button
          type="button"
          className="btn btn-outline-secondary rounded-pill"
          onClick={goHome}
        >
          Home
        </button>
      </div>

      {error && (
        <div className="alert alert-danger rounded-4" role="alert">
          {error}
        </div>
      )}

      {message && (
        <div className="alert alert-success rounded-4" role="status">
          {message}
        </div>
      )}

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border" role="status" />
          <div className="text-muted mt-2">Caricamento...</div>
        </div>
      ) : code ? (
        <>
          {data?.azienda && (
            <div className="card mb-3" style={cardStyle}>
              <div className="card-body p-4">
                <div className="small text-muted">{data.azienda.code}</div>
                <h3 className="fw-bold mb-3">{data.azienda.name}</h3>

                <button
                  type="button"
                  className="btn btn-success rounded-pill w-100 mb-3 fw-semibold"
                  onClick={() =>
                    navigate("/app/agente/ordini/nuovo", {
                      state: {
                        azienda: {
                          id: data.azienda.id,
                          name: data.azienda.name,
                          code: data.azienda.code,
                        },
                      },
                    })
                  }
                  disabled={!data.azienda.id}
                >
                  Nuovo ordine per questa azienda
                </button>


                {data.azienda.ref_contact && (
                  <div className="mb-2">
                    <span className="text-muted">Referente: </span>
                    {data.azienda.ref_contact}
                  </div>
                )}

                {data.azienda.phone && (
                  <div className="mb-2">
                    <span className="text-muted">Telefono: </span>
                    <a href={`tel:${data.azienda.phone}`}>
                      {data.azienda.phone}
                    </a>
                  </div>
                )}

                {data.azienda.email && (
                  <div className="mb-2">
                    <span className="text-muted">Email: </span>
                    <a href={`mailto:${data.azienda.email}`}>
                      {data.azienda.email}
                    </a>
                  </div>
                )}

                {(data.azienda.address ||
                  data.azienda.cap ||
                  data.azienda.citta ||
                  data.azienda.provincia) && (
                  <div>
                    <span className="text-muted">Indirizzo: </span>
                    {[
                      data.azienda.address,
                      data.azienda.cap,
                      data.azienda.citta,
                      data.azienda.provincia,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </div>
                )}

                <div className="border-top mt-3 pt-3">
                  <span className="text-muted small">Ultimo contatto</span>
                  <div className="fw-semibold">
                    {formatDate(data.ultimo_contatto)}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="card mb-3" style={cardStyle}>
            <div className="card-body p-4">
              <h4 className="fw-bold mb-3">Registra un contatto</h4>

              <form onSubmit={registraAttivita}>
                <div className="mb-3">
                  <label htmlFor="crm-tipo" className="form-label fw-semibold">
                    Tipo di attività
                  </label>
                  <select
                    id="crm-tipo"
                    className="form-select form-select-lg"
                    value={tipo}
                    onChange={(event) => setTipo(event.target.value)}
                    disabled={saving}
                  >
                    {TIPI_ATTIVITA.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label
                    htmlFor="crm-notes"
                    className="form-label fw-semibold"
                  >
                    Note del contatto
                  </label>
                  <textarea
                    id="crm-notes"
                    className="form-control"
                    rows={4}
                    maxLength={5000}
                    required
                    value={notes}
                    onChange={(event) => setNotes(event.target.value)}
                    placeholder="Esito della telefonata, visita, richiesta del cliente..."
                    disabled={saving}
                  />
                  <div className="form-text">
                    Massimo 5.000 caratteri.
                  </div>
                </div>

                <div className="mb-4">
                  <label
                    htmlFor="crm-next-contact"
                    className="form-label fw-semibold"
                  >
                    Prossimo appuntamento (facoltativo)
                  </label>
                  <input
                    id="crm-next-contact"
                    type="datetime-local"
                    className="form-control form-control-lg"
                    value={nextContact}
                    min={new Date(Date.now() + 60000)
                      .toISOString()
                      .slice(0, 16)}
                    onChange={(event) => setNextContact(event.target.value)}
                    disabled={saving}
                  />
                </div>

                <button
                  type="submit"
                  className="btn btn-primary btn-lg rounded-pill w-100"
                  disabled={saving || !notes.trim()}
                >
                  {saving ? "Salvataggio..." : "Registra attività"}
                </button>
              </form>
            </div>
          </div>

          <div className="card mb-3" style={cardStyle}>
            <div className="card-body p-4">
              <h4 className="fw-bold mb-3">Storico commerciale</h4>

              {!data?.storico?.length ? (
                <p className="text-muted mb-0">
                  Nessun contatto registrato per questa azienda.
                </p>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {data.storico.map((activity) => (
                    <div
                      key={activity.id}
                      className="border-start border-3 ps-3 py-1"
                    >
                      <div className="d-flex flex-wrap justify-content-between gap-2">
                        <span className="fw-bold">
                          {activityLabel(activity.tipo)}
                        </span>
                        <span className="text-muted small">
                          {formatDate(activity.occurred_at)}
                        </span>
                      </div>

                      <p className="mb-2 mt-2" style={{ whiteSpace: "pre-wrap" }}>
                        {activity.notes}
                      </p>

                      {activity.next_contact_at && (
                        <div className="small text-primary">
                          Prossimo contatto:{" "}
                          {formatDate(activity.next_contact_at)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="card" style={cardStyle}>
          <div className="card-body p-4">
            <div className="d-flex justify-content-between align-items-center gap-3 mb-3">
              <div>
                <h4 className="fw-bold mb-1">Da ricontattare</h4>
                <div className="text-muted small">
                  {data?.totale ?? 0} aziende da seguire
                </div>
              </div>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm rounded-pill"
                onClick={loadData}
              >
                Aggiorna
              </button>
            </div>

            {!data?.promemoria?.length ? (
              <p className="text-muted mb-0">
                Nessun promemoria commerciale al momento.
              </p>
            ) : (
              <div className="d-flex flex-column gap-3">
                {data.promemoria.map((item) => (
                  <div key={item.azienda_id} className="border rounded-4 p-3">
                    <div className="d-flex justify-content-between align-items-start gap-3">
                      <div style={{ minWidth: 0 }}>
                        <div className="fw-bold">{item.name}</div>
                        <div className="text-muted small">{item.code}</div>
                      </div>
                      <span className="badge text-bg-warning">
                        {item.stato === "MAI_CONTATTATA"
                          ? "Mai contattata"
                          : item.stato === "DA_RICONTATTARE"
                            ? "Oltre 30 giorni"
                            : "Promemoria"}
                      </span>
                    </div>

                    <div className="small mt-2">
                      Ultimo contatto: {formatDate(item.ultimo_contatto)}
                    </div>

                    {item.appuntamento_scaduto && (
                      <div className="small text-danger mt-1">
                        Appuntamento scaduto:{" "}
                        {formatDate(item.prossimo_contatto)}
                      </div>
                    )}

                    {item.phone && (
                      <div className="mt-2">
                        <a href={`tel:${item.phone}`}>{item.phone}</a>
                      </div>
                    )}

                    <button
                      type="button"
                      className="btn btn-primary rounded-pill w-100 mt-3"
                      onClick={() =>
                        navigate(
                          `/app/agente/aziende/${encodeURIComponent(item.code)}/crm`
                        )
                      }
                    >
                      Apri scheda commerciale
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
